import React from "react";
import "./previewArticle.scss";
import ArticleBanner from "../../assets/publicworks.jpg";
import { sanitizeArticleContent } from "./articleContentSanitizer";

const defaultArticle = {
  title: "Lorem Ipsum",
  createdAt: "19-05-2026",
  submittedByName: "Vishal Kora",
  tags: ["Ministry of Education"],
  image: ArticleBanner,
  articleContent: `
    <p>
      Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed suscipit
      ullamcorper faucibus. Donec eu dolor ultricies, posuere neque vitae,
      fringilla augue. Suspendisse nisl augue, pellentesque nec vulputate sed,
      placerat eu nisi.
    </p>
    <h2>Maecenas eu risus in augue(H1):</h2>
    <p>
      Maecenas eu risus in augue sollicitudin iaculis. In eget suscipit sapien.
      Ut pellentesque eros at nisi hendrerit, sed scelerisque nunc imperdiet.
      Fusce convallis, est quis imperdiet efficitur, lectus metus iaculis sapien,
      a venenatis ligula augue dignissim ex.
    </p>
  `
};

const getImageUrl = image => {
  if (!image) return null;
  if (typeof image === "string") return image.trim() || null;

  return image.s3Url || image.url || image.path || image.fileUrl || null;
};

const getArticleImage = article => {
  const articleImages = Array.isArray(article.images) ? article.images : [];
  const firstImage = articleImages.map(getImageUrl).find(Boolean);

  if (firstImage) return firstImage;

  const fallbackImage = Array.isArray(article.image)
    ? article.image.map(getImageUrl).find(Boolean)
    : getImageUrl(article.image);

  return fallbackImage || ArticleBanner;
};

const getArticleTags = article => {
  const articleTags = article.tags || article.tag || article.tagIds || [];

  if (!Array.isArray(articleTags) || !articleTags.length) {
    return [];
  }

  return articleTags
    .map(tag =>
      typeof tag === "string" || typeof tag === "number"
        ? String(tag)
        : tag && (tag.name || tag.label || tag.title || tag.value)
    )
    .filter(Boolean);
};

const formatArticleDate = value => {
  if (!value) return "";

  const rawDate = String(value).trim();
  const ddmmyyyyMatch = rawDate.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  const parsedDate = ddmmyyyyMatch
    ? new Date(
        Number(ddmmyyyyMatch[3]),
        Number(ddmmyyyyMatch[2]) - 1,
        Number(ddmmyyyyMatch[1])
      )
    : new Date(rawDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return rawDate;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

const AuthorIcon = ({ className }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="26"
    height="26"
    viewBox="0 0 26 26"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <g filter="url(#preview-author-filter)">
      <circle cx="13" cy="9" r="9" fill="#00C195" />
      <circle cx="13" cy="7.5" r="3" stroke="white" />
      <path
        d="M6.5 15.25C6.91667 14.6667 7.9 13.35 8.5 12.75C9.25 12 10.5 12 10.75 12C10.95 12 14.5 12 16.25 12C16.6667 12 17.65 12.15 18.25 12.75C18.85 13.35 19.6667 14.5 20 15"
        stroke="white"
      />
    </g>
    <defs>
      <filter
        id="preview-author-filter"
        x="0"
        y="0"
        width="26"
        height="26"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB"
      >
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        />
        <feOffset dy="4" />
        <feGaussianBlur stdDeviation="2" />
        <feComposite in2="hardAlpha" operator="out" />
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0.756863 0 0 0 0 0.584314 0 0 0 0.3 0"
        />
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow"
        />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow"
          result="shape"
        />
      </filter>
    </defs>
  </svg>
);

const PreviewArticle = ({ article = defaultArticle }) => {
  const articleTitle = article.title;
  const articleDate = article.createdAt;
  const articleAuthor =
    article.submittedByName || article.submittedBy || article.author;
  const articleTags = getArticleTags(article);
  const articleImage = getArticleImage(article);
  const articleContent = article.articleContent || article.content || "";

  return (
    <main className="preview-article-page">
      <section className="preview-article-hero">
        <h1>{articleTitle}</h1>
        <div className="preview-article-meta">
          <span>{formatArticleDate(articleDate)}</span>
          <span className="preview-article-meta-divider" />
          <span className="preview-article-author">
            <AuthorIcon className="preview-article-author-icon" />
            {articleAuthor}
          </span>
        </div>
        <div className="preview-article-tags">
          {articleTags.map(tag => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <img
          src={articleImage}
          alt={articleTitle || "Article banner"}
          className="preview-article-banner"
          onError={event => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = ArticleBanner;
          }}
        />
      </section>

      <section className="preview-article-content-wrap preview-article-content-only">
        <article
          className="preview-article-content"
          dangerouslySetInnerHTML={{
            __html: sanitizeArticleContent(articleContent)
          }}
        />
      </section>
    </main>
  );
};

export default PreviewArticle;
